"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useUser } from "@/hooks/use-user";
import { orpc } from "@/utils/orpc";

type WishlistItem = {
  productId: string;
  variantId: string;
  customerId: string;
  addedAt: string;
};

export function useWishlist() {
  const { accessToken, isAuthenticated } = useUser();
  const queryClient = useQueryClient();

  // Fetch wishlist from backend
  const {
    data,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["wishlist", accessToken] as const,
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("Access token is required");
      }

      return orpc.shopify.getWishlist.call({
        customerAccessToken: accessToken,
      });
    },
    enabled: isAuthenticated && !!accessToken,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  // Add to wishlist mutation
  const addMutation = useMutation({
    mutationFn: async ({
      productId,
      variantId,
    }: {
      productId: string;
      variantId: string;
    }) => {
      if (!accessToken) {
        throw new Error("Access token is required");
      }

      return orpc.shopify.addToWishlist.call({
        customerAccessToken: accessToken,
        productId,
        variantId,
      });
    },
    onMutate: async ({ productId, variantId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["wishlist", accessToken] });

      // Snapshot previous value
      const previousWishlist = queryClient.getQueryData<WishlistItem[]>([
        "wishlist",
        accessToken,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData<WishlistItem[]>(
        ["wishlist", accessToken],
        (old) => {
          if (!old) {
            return [
              {
                productId,
                variantId,
                customerId: "",
                addedAt: new Date().toISOString(),
              },
            ];
          }
          const exists = old.some(
            (item) =>
              item.productId === productId && item.variantId === variantId
          );
          if (exists) return old;
          return [
            ...old,
            {
              productId,
              variantId,
              customerId: "",
              addedAt: new Date().toISOString(),
            },
          ];
        }
      );

      // Return context with previous value
      return { previousWishlist };
    },
    onSuccess: (response, _variables, context) => {
      if (response.success) {
        // Update cache with server response
        queryClient.setQueryData<WishlistItem[]>(
          ["wishlist", accessToken],
          response.wishlist
        );
      } else {
        // Rollback optimistic update on server error
        if (context?.previousWishlist) {
          queryClient.setQueryData<WishlistItem[]>(
            ["wishlist", accessToken],
            context.previousWishlist
          );
        }
        toast.error("Failed to add to wishlist", {
          description: response.error || "Unknown error occurred",
        });
      }
    },
    onError: (error: Error, _variables, context) => {
      // Rollback on error
      if (context?.previousWishlist) {
        queryClient.setQueryData<WishlistItem[]>(
          ["wishlist", accessToken],
          context.previousWishlist
        );
      }
      toast.error("Failed to add to wishlist", {
        description: error.message,
      });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["wishlist", accessToken] });
    },
  });

  // Remove from wishlist mutation
  const removeMutation = useMutation({
    mutationFn: async ({
      productId,
      variantId,
    }: {
      productId: string;
      variantId: string;
    }) => {
      if (!accessToken) {
        throw new Error("Access token is required");
      }

      return orpc.shopify.removeFromWishlist.call({
        customerAccessToken: accessToken,
        productId,
        variantId,
      });
    },
    onMutate: async ({ productId, variantId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["wishlist", accessToken] });

      // Snapshot previous value
      const previousWishlist = queryClient.getQueryData<WishlistItem[]>([
        "wishlist",
        accessToken,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData<WishlistItem[]>(
        ["wishlist", accessToken],
        (old) => {
          if (!old) return [];
          return old.filter(
            (item) =>
              !(item.productId === productId && item.variantId === variantId)
          );
        }
      );

      // Return context with previous value
      return { previousWishlist };
    },
    onSuccess: (response, _variables, context) => {
      if (response.success) {
        // Update cache with server response
        queryClient.setQueryData<WishlistItem[]>(
          ["wishlist", accessToken],
          response.wishlist
        );
      } else {
        // Rollback optimistic update on server error
        if (context?.previousWishlist) {
          queryClient.setQueryData<WishlistItem[]>(
            ["wishlist", accessToken],
            context.previousWishlist
          );
        }
        toast.error("Failed to remove from wishlist", {
          description: response.error || "Unknown error occurred",
        });
      }
    },
    onError: (error: Error, _variables, context) => {
      // Rollback on error
      if (context?.previousWishlist) {
        queryClient.setQueryData<WishlistItem[]>(
          ["wishlist", accessToken],
          context.previousWishlist
        );
      }
      toast.error("Failed to remove from wishlist", {
        description: error.message,
      });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["wishlist", accessToken] });
    },
  });

  // Get wishlist items
  const items = useMemo(() => data ?? [], [data]);

  // Check if product+variant is in wishlist
  const has = useCallback(
    (productId: string, variantId?: string) => {
      if (!variantId) {
        // Check if any variant of this product is in wishlist
        return items.some((item) => item.productId === productId);
      }
      return items.some(
        (item) => item.productId === productId && item.variantId === variantId
      );
    },
    [items]
  );

  // Add product to wishlist
  const add = useCallback(
    async (productId: string, variantId: string) => {
      if (!(isAuthenticated && accessToken)) {
        toast.error("Please sign in to add items to your wishlist");
        return;
      }

      try {
        await addMutation.mutateAsync({ productId, variantId });
      } catch {
        // Error already handled in mutation callbacks
      }
    },
    [isAuthenticated, accessToken, addMutation]
  );

  // Remove product from wishlist
  const remove = useCallback(
    async (productId: string, variantId: string) => {
      if (!(isAuthenticated && accessToken)) {
        toast.error("Please sign in to manage your wishlist");
        return;
      }

      try {
        await removeMutation.mutateAsync({ productId, variantId });
      } catch {
        // Error already handled in mutation callbacks
      }
    },
    [isAuthenticated, accessToken, removeMutation]
  );

  // Toggle product in wishlist
  const toggle = useCallback(
    async (productId: string, variantId: string) => {
      return has(productId, variantId)
        ? remove(productId, variantId)
        : add(productId, variantId);
    },
    [has, add, remove]
  );

  // Loading state
  const loading = useMemo(
    () => isLoading || addMutation.isPending || removeMutation.isPending,
    [isLoading, addMutation.isPending, removeMutation.isPending]
  );

  // Error state
  const error = useMemo(
    () => queryError || addMutation.error || removeMutation.error,
    [queryError, addMutation.error, removeMutation.error]
  );

  // Return hook interface
  return useMemo(
    () => ({ items, has, add, remove, toggle, loading, error }),
    [items, has, add, remove, toggle, loading, error]
  );
}
